import React, {
  FunctionComponent, useCallback, useRef,
} from 'react';
import {
  Card, ICON_MAGNIFY, Message, Stack,
} from '@doveit/bricks';
import FullTextSearchForm, { FullTextSearchFormModel } from '../../../components/full-text-search-form/FullTextSearchForm';
import useSearchContacts from '../../hooks/use-search-contacts/useSearchContacts';
import usePagination from '../../../hooks/use-pagination/usePagination';
import PaginatedList from '../../../components/paginated-list/PaginatedList';
import SearchContactCard from '../../containers/search-contact-card/SearchContactCard';
import useFilters, { Filters } from '../../../hooks/use-filters/useFilters';
import SimplePageLayout from '../../../layouts/simple-page-layout/SimplePageLayout';

interface SearchContactPageFilters extends Filters {
  q?: string;
}

export const CONTACT_SEARCH_ERROR_MESSAGE = 'Errore durante la ricerca del contatto';

const SearchContactPage: FunctionComponent = () => {
  const { filters: { q: searchQuery }, addFilters } = useFilters<SearchContactPageFilters>();
  const scrollRef = useRef<HTMLDivElement>(null);
  const contactsPagination = usePagination<HTMLDivElement>('page', scrollRef);

  const { data: contacts, error: contactsError } = useSearchContacts(searchQuery, {
    size: 20,
    page: contactsPagination.page,
  });

  const onSearchSubmit = useCallback(({ search }: FullTextSearchFormModel) => {
    const newFilter: SearchContactPageFilters = {
      q: search?.trim(),
    };
    addFilters(newFilter, { deleteKeys: [contactsPagination.paginationQueryParam] });
  }, [addFilters, contactsPagination.paginationQueryParam]);

  const isSearching = !!searchQuery && !contacts && !contactsError;

  return (
    <SimplePageLayout>
      <Card paddingSize="M">
        <Card.Header
          icon={{ path: ICON_MAGNIFY }}
          title="Cerca contatto"
        />
        <Card.Content>
          <Stack gap={300}>
            <FullTextSearchForm
              onSubmit={onSearchSubmit}
              initialValues={{ search: searchQuery }}
              placeholder="Cerca per nome, telefono o email"
              loading={isSearching}
            />
            {!contacts && contactsError && (
              <Message
                type="critical"
                message={CONTACT_SEARCH_ERROR_MESSAGE}
              />
            )}
            {contacts && (
              <PaginatedList
                {...contacts}
                query={searchQuery}
                goToPage={contactsPagination.goToPage}
              >
                {contacts.content.map((contact) => (
                  <SearchContactCard
                    key={contact.id}
                    contact={contact}
                  />
                ))}
              </PaginatedList>
            )}
          </Stack>
        </Card.Content>
      </Card>
    </SimplePageLayout>
  );
};

export default SearchContactPage;
